import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/pages/home/components/MovieCard';
import { categories, getMoviesByCategory, genresList } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

const ITEMS_PER_PAGE = 12;

function getCategoryLabel(key: string): string {
  const map: Record<string, string> = {
    all: '全部内容',
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
  };
  return map[key] || key;
}

export default function CategoryPage() {
  const { category = 'all' } = useParams<{ category: string }>();
  const [activeGenre, setActiveGenre] = useState('全部');
  const [currentPage, setCurrentPage] = useState(1);

  const allMovies = getMoviesByCategory(category);
  const filtered = activeGenre === '全部'
    ? allMovies
    : allMovies.filter(m => m.genres.includes(activeGenre));

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedMovies = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Scroll to top when category or page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [category, currentPage]);

  // Reset page when category or genre changes
  useEffect(() => {
    setCurrentPage(1);
  }, [category, activeGenre]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-6" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-400 hover:text-primary-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-500 text-sm"></i>
              <span className="text-foreground-200 font-medium">{getCategoryLabel(category)}</span>
            </nav>

            {/* Page Title */}
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-50 mb-1">
              {getCategoryLabel(category)}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">共 {allMovies.length} 部作品</p>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    category === cat.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-50 hover:bg-background-200'
                  }`}
                >
                  {cat.label}
                </Link>
              ))}
            </div>

            {/* Genre Filter */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-1 flex-wrap">
              <button
                onClick={() => setActiveGenre('全部')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeGenre === '全部'
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-50 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {genresList.map((genre) => {
                const count = allMovies.filter(m => m.genres.includes(genre)).length;
                if (count === 0) return null;
                return (
                  <button
                    key={genre}
                    onClick={() => setActiveGenre(genre)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeGenre === genre
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-50 hover:bg-background-200'
                    }`}
                  >
                    {genre} ({count})
                  </button>
                );
              })}
            </div>

            {/* Movie Grid */}
            {paginatedMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5 mb-10">
                {paginatedMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-foreground-500">
                <i className="ri-emotion-sad-line text-5xl mb-4"></i>
                <p className="text-base">暂无相关内容</p>
                <p className="text-sm mt-1">试试切换其他分类或类型</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pb-14">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:text-foreground-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer ${
                      page === currentPage
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:text-foreground-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Schema.org Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.chunyecinema.com/" },
            { "@type": "ListItem", "position": 2, "name": getCategoryLabel(category), "item": `https://www.chunyecinema.com/category/${category}` }
          ]
        }) }} />
      </main>

      <Footer />
    </div>
  );
}